"use strict";

import "core-js/modules/es.string.starts-with.js";
/**
 * Utility functions for managing MFE mount functions
 */
import { debouncedFunction } from '@autoguru/debounce-utils';
const MOUNT_PREFIX = '__MOUNT__';
const WORKSPACE_PREFIX = '@autoguru/';

/**
 * Normalizes an MFE name by removing the workspace prefix if present
 * @param mfeName The name of the MFE
 * @returns The normalized MFE name
 */
const normalizeMFEName = mfeName => {
  return mfeName.startsWith(WORKSPACE_PREFIX) ? mfeName.slice(WORKSPACE_PREFIX.length) : mfeName;
};

/**
 * Checks if a mount function exists for a given MFE name
` * @param mfeName The name of the MFE
 * @returns The mount function if it exists, null otherwise
 */
const getMountFunctionFromWindow = mfeName => {
  const mfeKey = `${MOUNT_PREFIX}${mfeName}`;
  const mfe = window[mfeKey];
  if (!mfe) {
    return {
      mountFunction: null,
      getMountPoint: null
    };
  }
  return {
    mountFunction: mfe.mountFunction,
    getMountPoint: mfe.getMountPoint
  };
};

/**
 * Sets the mount function for a specific MFE
 * @param mfeName The name of the MFE
 * @param mountFunction The function to mount the MFE
 * @param getMountPoint Function that returns the DOM element to mount the MFE into
 */
export const setMountFunction = (mfeName, mountFunction, getMountPoint) => {
  const normalizedName = normalizeMFEName(mfeName);
  window[`${MOUNT_PREFIX}${normalizedName}`] = {
    mountFunction: debouncedFunction(200, mountFunction),
    getMountPoint
  };
};

/**
 * Validates if the mount target is available and not already mounted
 * @param getMountPoint Function that returns the DOM element to mount the MFE into
 * @returns true if the mount target is valid, false otherwise
 */
export const validateMountTarget = getMountPoint => {
  const mountTarget = getMountPoint();
  if (!mountTarget) {
    return false;
  }
  const existingAppName = mountTarget.getAttribute('data-mfe-app-name');
  return !(existingAppName && mountTarget.childNodes.length > 0);
};

/**
 * Gets the mount function for a specific MFE
 * @param mfeName The name of the MFE
 * @returns The mount function if it exists, null otherwise
 */
export const getMountFunction = mfeName => {
  const normalizedName = normalizeMFEName(mfeName);
  const {
    mountFunction,
    getMountPoint
  } = getMountFunctionFromWindow(normalizedName);
  return mountFunction ? () => {
    try {
      if (!validateMountTarget(getMountPoint)) {
        return false;
      }
      return mountFunction;
    } catch (_unused) {
      return console.info(`${mfeName} mount attempt rejected`);
    }
  } : null;
};
export const getMountPointElement = mfeName => {
  const normalizedName = normalizeMFEName(mfeName);
  const {
    getMountPoint
  } = getMountFunctionFromWindow(normalizedName);
  return typeof getMountPoint === 'function' ? getMountPoint() : null;
};